/**
 * SiteRouter — Renders a tenant's live site at /s/:subdomain/* paths.
 *
 * In production, tenants access via subdomain.partyrentalsuite.com (handled
 * by SubdomainRouter). In preview/dev, we simulate this with /s/{subdomain}
 * paths so the URL stays clean (no /preview-* prefix).
 *
 * Phase 6 (Theme Builder Consolidation, 2026-04-27) retired the 9 hardcoded
 * React themes and the v1/v2 fork in this file. Every tenant site now
 * renders through the token-driven SiteRouterV2 — no exceptions.
 *
 * The `theme_renderer_version` column on `microsites` is kept in the
 * database for historical purposes but is no longer read here.
 *
 * URL override: `?renderer=v1` or `?renderer=v2` is accepted but ignored
 * in this build. The Builder's compare panel still passes it for forward
 * compatibility — both branches now produce the same v2 render.
 */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { usePublicSite } from "../contexts/PublicSiteContext";
import ComingSoonPlaceholder from "./ComingSoonPlaceholder";

const PLATFORM_DOMAIN = process.env.REACT_APP_PLATFORM_DOMAIN || "partyrentalsuite.com";

const SiteRouterV2 = React.lazy(() => import("./SiteRouterV2"));

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function SiteRouter() {
  const { subdomain } = useParams();
  const { configure } = usePublicSite();
  const location = useLocation();
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const resolvedRef = useRef(null);

  useEffect(() => {
    if (!subdomain || resolvedRef.current === subdomain) return;
    resolvedRef.current = subdomain;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${backendUrl}/api/public/site/resolve?domain=${subdomain}.${PLATFORM_DOMAIN}`)
      .then((r) => {
        if (!r.ok) throw new Error("Site not found");
        return r.json();
      })
      .then((data) => {
        setSiteData(data);
        localStorage.setItem("public_tenant_id", data.tenant_id);
        localStorage.setItem("public_microsite_id", data.microsite_id);
        configure(data.tenant_id, data.microsite_id);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [subdomain]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Loader />;

  if (error || !siteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="site-router-not-found">
        <div className="text-center max-w-sm">
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Site Not Found</h1>
          <p className="text-sm text-slate-500 mb-4">
            No site found for <strong>{subdomain}</strong>
          </p>
        </div>
      </div>
    );
  }

  // ─── Draft gate ───
  // Public visitors see a branded Coming Soon page (with noindex). Tenant
  // admins (valid JWT) and ?preview=true bypass the gate.
  if (siteData.status === "draft") {
    const hasAdminJwt = !!(localStorage.getItem("access_token") || localStorage.getItem("tenant_token"));
    const previewParam = new URLSearchParams(location.search).get("preview") === "true";
    if (!hasAdminJwt && !previewParam) {
      return <ComingSoonPlaceholder resolved={siteData} />;
    }
  }

  // Always v2 — Phase 6 retired the legacy renderer.
  return (
    <React.Suspense fallback={<Loader />}>
      <SiteRouterV2 siteData={siteData} subdomain={subdomain} />
    </React.Suspense>
  );
}
